import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonText,
  IonSpinner,
  IonIcon,
  IonToast,
  IonRouterLink,
} from '@ionic/react';
import { mailOutline, checkmarkCircle } from 'ionicons/icons';
import { LogoLink } from '@/components/LogoLink';
import { resendVerificationEmail } from '@/lib/api/auth';
import { useAuthStore } from '@/stores/useAuthStore';
import { AppFooter } from '@/components/AppFooter';
import { useEffect } from 'react';
import { setSeo, resetSeo } from '@/lib/seo';
import { stashReturnPath } from '@/lib/return-path';

export function VerifyEmailPage() {
  const location = useLocation<{ email?: string }>();
  const params = new URLSearchParams(location.search);
  const email = params.get('email') || location.state?.email || '';
  const verified = params.get('verified') === '1';

  useEffect(() => {
    setSeo({ robots: 'noindex, nofollow' });
    return () => resetSeo();
  }, []);

  // Spec §6 — role-aware landing after email verification: employers on
  // /employer, candidates and both on the Get Started quest (unknown role
  // falls back to the candidate landing).
  // @see Documentation/Specification/04_Processes/application-specific/workseeking/CandidateAndEmployerRegistration.md §6
  const roleSelection = useAuthStore((s) => s.roleSelection);
  useEffect(() => {
    if (verified) {
      stashReturnPath(roleSelection === 'employer' ? '/employer' : '/profile?welcome=1');
    }
  }, [verified, roleSelection]);

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  const handleResend = async () => {
    if (!email) {
      setToast({ msg: 'Enter your email on the sign-in page to resend.', color: 'danger' });
      return;
    }
    setLoading(true);
    try {
      await resendVerificationEmail(email);
      setToast({ msg: `Verification link sent to ${email}.`, color: 'success' });
    } catch {
      setToast({ msg: 'Could not resend. Try again shortly.', color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <LogoLink style={{ height: '28px', marginTop: '4px' }} />
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="bp-text-center ion-padding-top">
          {verified ? (
            <IonIcon icon={checkmarkCircle} color="success" style={{ fontSize: '3.5rem' }} />
          ) : (
            <IonIcon icon={mailOutline} color="primary" style={{ fontSize: '3.5rem' }} />
          )}
          <h1 className="bp-heading">
            {verified ? 'Email verified!' : 'Verify your email'}
          </h1>
          <IonText color="medium">
            {verified ? (
              <p>Your email is confirmed. You can now sign in and use BluePages.</p>
            ) : email ? (
              <p>
                We sent a verification link to <strong>{email}</strong>. Open it to activate
                your account.
              </p>
            ) : (
              <p>We sent a verification link to your email. Open it to activate your account.</p>
            )}
          </IonText>
        </div>

        {!verified && (
          <IonButton expand="block" className="ion-margin-top" onClick={handleResend} disabled={loading}>
            {loading ? <IonSpinner name="dots" /> : 'Resend verification email'}
          </IonButton>
        )}

        <div className="bp-text-center ion-padding-top">
          <IonRouterLink routerLink="/signin">Back to sign in</IonRouterLink>
        </div>

        <IonToast
          isOpen={!!toast}
          message={toast?.msg}
          color={toast?.color}
          duration={3000}
          onDidDismiss={() => setToast(null)}
        />
            <AppFooter />

      </IonContent>
    </IonPage>
  );
}
